import type { GeometryKey } from '../locales/keys';

export type CoordMode = 'segment' | 'parallel-perpendicular' | 'section' | 'shoelace' | 'locus';

export interface CoordPreset {
  id: string;
  mode: CoordMode;
  labelKey: GeometryKey;
  advancedOnly: boolean;
  // segment / parallel-perpendicular config
  ptA?: [number, number];
  ptB?: [number, number];
  ptC?: [number, number];
  ptD?: [number, number];
  // section config
  sectionM?: number;
  sectionN?: number;
  // shoelace config
  polygonVertices?: [number, number][];
}

export const COORD_PRESETS: readonly CoordPreset[] = [
  {
    id: '3-4-5-segment',
    mode: 'segment',
    labelKey: 'coord.preset.3-4-5-segment',
    advancedOnly: false,
    ptA: [2, 8],
    ptB: [6, 5],
  },
  {
    id: 'perpendicular-pair',
    mode: 'parallel-perpendicular',
    labelKey: 'coord.preset.perpendicular-pair',
    advancedOnly: false,
    ptA: [2, 3],
    ptB: [6, 5],
    ptC: [3, 8],
    ptD: [5, 4],
  },
  {
    id: 'section-2-1',
    mode: 'section',
    labelKey: 'coord.preset.section-2-1',
    advancedOnly: true,
    ptA: [0, 0],
    ptB: [9, 6],
    sectionM: 2,
    sectionN: 1,
  },
  {
    id: 'lattice-pentagon',
    mode: 'shoelace',
    labelKey: 'coord.preset.lattice-pentagon',
    advancedOnly: true,
    polygonVertices: [
      [5, 2],
      [9, 4],
      [8, 8],
      [2, 8],
      [1, 4],
    ],
  },
];

export function isValidCoordPreset(p: CoordPreset): boolean {
  if (typeof p.id !== 'string' || p.id.length === 0) return false;
  const validModes: CoordMode[] = [
    'segment',
    'parallel-perpendicular',
    'section',
    'shoelace',
    'locus',
  ];
  if (!validModes.includes(p.mode)) return false;
  if (typeof p.labelKey !== 'string' || p.labelKey.length === 0) return false;
  if (typeof p.advancedOnly !== 'boolean') return false;
  if (p.ptA !== undefined) {
    if (!Array.isArray(p.ptA) || p.ptA.length !== 2) return false;
    if (typeof p.ptA[0] !== 'number' || typeof p.ptA[1] !== 'number') return false;
  }
  if (p.ptB !== undefined) {
    if (!Array.isArray(p.ptB) || p.ptB.length !== 2) return false;
    if (typeof p.ptB[0] !== 'number' || typeof p.ptB[1] !== 'number') return false;
  }
  if (p.ptC !== undefined) {
    if (!Array.isArray(p.ptC) || p.ptC.length !== 2) return false;
    if (typeof p.ptC[0] !== 'number' || typeof p.ptC[1] !== 'number') return false;
  }
  if (p.ptD !== undefined) {
    if (!Array.isArray(p.ptD) || p.ptD.length !== 2) return false;
    if (typeof p.ptD[0] !== 'number' || typeof p.ptD[1] !== 'number') return false;
  }
  if (p.polygonVertices !== undefined) {
    if (!Array.isArray(p.polygonVertices)) return false;
    if (p.polygonVertices.length < 3 || p.polygonVertices.length > 6) return false;
    for (const v of p.polygonVertices) {
      if (!Array.isArray(v) || v.length !== 2) return false;
      if (typeof v[0] !== 'number' || typeof v[1] !== 'number') return false;
    }
  }
  if (p.sectionM !== undefined && typeof p.sectionM !== 'number') return false;
  if (p.sectionN !== undefined && typeof p.sectionN !== 'number') return false;
  return true;
}

export function validateCoordPresetIds(presets: readonly CoordPreset[]): boolean {
  const ids = new Set<string>();
  for (const preset of presets) {
    if (ids.has(preset.id)) return false;
    ids.add(preset.id);
  }
  return true;
}
